import { useState, useRef, useEffect } from "react";
import { FaBan, FaDeleteLeft, FaMicrophone, FaMicrophoneSlash, FaPhone, FaVolumeHigh, FaVolumeXmark } from "react-icons/fa6";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

// Panel
import Icomput from "src/components/icomput";
import Tooltip from "src/components/tooltip";

// Helpers
import { Caret, filterPhoneNumbersChars, playDTMFTone } from "src/helpers";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { type DialerInstance, DialerStatus } from "src/services/dialer";

// Actions
import { updateExternalCall } from "src/store/call/actions";
import { setPstnAsActive, updatePstnCall } from "src/store/call/reducer";
import PSTNCall from "src/models/pstn-call";
import { trackEvent } from "src/services/logger";

const DialerButton = ({ label, clickValue, onClick }: { label: any; clickValue?: any; onClick: Function }) => (
  <button
    type="button"
    className="flex items-center max-h-[54px] h-full justify-center py-2 text-lg text-neutral-gray-500 font-sora bg-neutral-gray-5 rounded-md hover:bg-neutral-gray-500 hover:text-white transition-all active:bg-primary-blue-800 active:outline-0 focus:outline-0"
    onClick={(evt) => {
      evt.preventDefault();
      onClick(clickValue || label);
    }}
  >
    {label}
  </button>
);

export default function FloatDialer({ dialer }: { dialer?: DialerInstance }) {
  if (!dialer) {
    alert("Sem instancia discador para continuar.");

    return null;
  }

  const dispatch = useAppDispatch();

  // Redux Selector
  const { inputDeviceId } = useAppSelector((state) => state.media);
  const { callDetails } = useAppSelector((state) => state.call);

  // Substates
  const [number, setNumber] = useState(dialer.getInput());
  const [status, setStatus] = useState<DialerStatus>(dialer.getStatus());
  const [isOutputMuted, setOutputState] = useState(dialer.isOutputMuted());
  const [isInputMuted, setInputState] = useState(dialer.isInputMuted());

  const [state, setState] = useState<{
    updatingCurrentCall: boolean;
    numberInputCaret: Caret | null;
  }>({
    updatingCurrentCall: false,
    numberInputCaret: null,
  });

  // Carrosel Things
  const activeNumber = callDetails?.pstnNumbers.find((pstn) => pstn.active);
  const pstnLefover: PSTNCall[] = activeNumber && callDetails?.pstnNumbers.length ? callDetails?.pstnNumbers.filter((n) => n.internalId !== activeNumber.internalId && !n.called) : [];

  const numberInputElmRef = useRef(null);

  const dialerBinds: Array<number> = [];

  useEffect(() => {
    dialer.updateCurrentNumber(number);
  }, [number]);

  useEffect(() => {
    dialerBinds.push(
      dialer.on("updateStatus", ({ args }: any) => {
        setStatus(args.status);

        if (args.error) toast.error(args.error.message);
      }),
    );

    dialerBinds.push(
      dialer.on("onCallHangup", ({ args }: any) => {
        setOutputState(false);
        setInputState(false);
      }),
    );

    return () => {
      dialer.clearBinds(dialerBinds);
    };
  }, []);

  // Create caret
  useEffect(() => {
    if (numberInputElmRef.current) setState((s) => ({ ...s, numberInputCaret: new Caret(numberInputElmRef.current!) }));
  }, [numberInputElmRef]);

  const connecting = [DialerStatus.CALLING, DialerStatus.CONNECTED, DialerStatus.CONNECTING, DialerStatus.CALLING].includes(status);
  const onCall = ![DialerStatus.DISCONNECTED, DialerStatus.DISCONNECTING, DialerStatus.IDLE].includes(status) && !connecting;

  // Fonte de verdade: sessão SIP real, caso o status esteja dessincronizado.
  const actuallyOnCall = onCall || !!dialer.isOnCall?.();

  const handleHangup = () => {
    trackEvent("outbound_call_hangup_requested");
    return dialer.callHangup();
  };
  const handleCall = async () => {
    // Calling or on call
    if (actuallyOnCall || connecting) return handleHangup();

    trackEvent("outbound_call_requested", { call_type: "outbound" });
    return await dialer.callTo(number, inputDeviceId);
  };

  // Press Controls
  const press = async (digit: any) => {
    playDTMFTone(digit);

    if (dialer.isOnCall?.()) return dialer.sendDTMF(digit);

    const pos = state.numberInputCaret?.getPos() || 0;
    const newValue = number.slice(0, pos) + digit + number.slice(pos);

    setNumber(newValue);
    // await sleep(200);
    return state.numberInputCaret?.setPos(pos + 1);
  };

  const erase = () => {
    const pos = state.numberInputCaret?.getPos() || 0;
    const newCaretPos = pos <= 0 ? 0 : pos - 1;

    if (pos === number.length) {
      setNumber((n) => n.substring(0, newCaretPos));
    } else {
      setNumber((n) => n.substring(0, newCaretPos) + n.substring(pos));
    }

    state.numberInputCaret?.setPos(newCaretPos);
  };

  const updateExternalProtocol = async (protocolo: string) => {
    const aderenteSlug = callDetails?.aderenteSlug;
    const currentPstnCall = activeNumber;

    if (!aderenteSlug || !currentPstnCall?.externalId) return toast.error("Falha ao registrar protocolo da chamada externa.");

    const { success } = await updateExternalCall(callDetails?.aderenteSlug, currentPstnCall.externalId, {
      protocolo,
      tipo_ligacao: "efetuada",
    });

    if (!success) return toast.error("Falha ao registrar protocolo da chamada externa.");

    currentPstnCall.protocol = protocolo;

    // Update
    dispatch(updatePstnCall(currentPstnCall));
  };

  // Carrossel Things
  const getNextPstnNumber = () => {
    if (!pstnLefover.length) return false;

    if ((activeNumber && activeNumber.called && !onCall) || !connecting) {
      const nextNumber = pstnLefover[0];
      dispatch(setPstnAsActive(nextNumber));
      // dialer.updateCurrentCall(nextNumber.requestedNumber);
      setNumber(nextNumber.requestedNumber!);
    } else {
      toast.error(`Defina um status para o número atual.`);
    }
  };

  const markNumberNotMet = async () => {
    const currentCall = callDetails?.pstnNumbers.find((pstn) => pstn.active);

    if (!currentCall) return toast.error("Nenhuma chamada para marcar como não atendida");

    const confirm = await Swal.fire({
      title: "Deseja marcar o número como não atendido?",
      text: "Caso confirme a ligação será marcada como efetuada e não atendida.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Marcar como não atendido",
      cancelButtonText: "Cancelar",
    });

    if (!confirm.isConfirmed) return;

    setState((s) => ({ ...s, updatingCurrentCall: true }));

    const ramal = dialer.getRamal();

    const { success, data } = await updateExternalCall(callDetails?.aderenteSlug!, currentCall.externalId!, {
      tipo_ligacao: "efetuada",
      status: "numero_ignorado",
      ...(ramal ? { ramal_chamada: ramal } : {}),
    });

    setState((s) => ({ ...s, updatingCurrentCall: false }));

    if (success) {
      currentCall.called = true;
      dispatch(updatePstnCall(currentCall));
    } else {
      toast.error("Falha ao marcar número como não atendido. Tente novamente.");
    }
  };

  // Tratando o conteúdo de colar antes de entrar no display do discador
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text");
    const contentFiltered = filterPhoneNumbersChars(text);

    setNumber(contentFiltered);
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const contentFiltered = filterPhoneNumbersChars(e.target.value);

    setNumber(contentFiltered);
  };

  return (
    <>
      <div className="top-group gap-8 flex flex-col overflow-y-auto grow">
        {/* Input */}
        <div className="w-full bg-neutral-gray-5 rounded-md px-6 py-3 flex gap-2 max-h-[52px] h-full">
          <input
            id="dialer-input"
            ref={numberInputElmRef}
            value={number}
            onChange={handleOnChange}
            onPaste={handlePaste}
            disabled={actuallyOnCall || connecting}
            placeholder="00000000000"
            type="text"
            className="flex-1 text-neutral-gray-600 bg-transparent text-sm font-sora tracking-[8px] focus:outline-0 disabled:cursor-no-drop"
          />
          <button onClick={erase} type="button" className="border-0 p-0 text-neutral-gray-600 disabled:cursor-no-drop" disabled={actuallyOnCall || connecting}>
            <FaDeleteLeft size={20} />
          </button>
        </div>

        {/* Dialer grid */}
        <div className="grid gap-2 grid-cols-3 grid-rows-4 mb-0 flex-1 max-h-[260px] auto-rows-fr">
          {["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"].map((v) => (
            <DialerButton key={v} label={v} onClick={(digit: any) => press(digit)} />
          ))}

          <DialerButton
            label={isOutputMuted ? <FaVolumeXmark /> : <FaVolumeHigh />}
            clickValue={"audioToggle"}
            onClick={() => {
              dialer.toggleOutputMute();
              setOutputState(!isOutputMuted);
            }}
          />
          <div></div>
          <DialerButton
            label={isInputMuted ? <FaMicrophoneSlash /> : <FaMicrophone />}
            clickValue={"micToggle"}
            onClick={() => {
              dialer.toggleInputMute();
              setInputState(!isInputMuted);
            }}
          />
        </div>

        {/* Carrossel */}
        {callDetails?.pstnNumbers && callDetails?.pstnNumbers.length > 0 && callDetails?.features && callDetails.features.includes("CARROSSEL") && (
          <div className="carrossel flex flex-col gap-2 mb-2">
            <div className="numbers flex flex-col gap-1">
              <strong>Número solicitado</strong>

              <div className="flex justify-between p-2 border border-gray-300 rounded-md items-center">
                {activeNumber?.requestedNumber}

                <button
                  type="button"
                  disabled={!pstnLefover.length}
                  onClick={getNextPstnNumber}
                  className="text-xs py-1 px-2 rounded-md bg-primary-blue-500 text-white hover:bg-primary-blue-400 disabled:bg-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  Próximo número {`>`}
                </button>
              </div>
            </div>
            {activeNumber?.called && (
              <div className="protocol-input flex flex-col gap-1">
                <strong>Protocolo</strong>
                <Icomput name="protocolo" placeholder="Ex: 000000" onBlur={(e) => updateExternalProtocol(e.target.value)} />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col items-center gap-2">
        <div className="actions flex gap-4 w-full">
          <button
            disabled={!number || connecting || status === DialerStatus.HANGUPING}
            onClick={handleCall}
            className={`h-10 w-full text-base font-bold text-white flex items-center 
              justify-center rounded-lg  disabled:bg-gray-300 disabled:text-gray-400 transition-all
              ${actuallyOnCall && "bg-feedback-negative-500 hover:bg-[#D63E2E]"}
              ${!actuallyOnCall && !connecting && "bg-primary-blue-400 hover:bg-primary-blue-500"}
              ${status === DialerStatus.INVITED && "bg-orange-400 hover:bg-orange-600"}`}
            type="button"
          >
            {actuallyOnCall && status !== DialerStatus.INVITED && <FaPhone className="rotate-[135deg]" />}
            {connecting && "Conectando ramal..."}
            {status === DialerStatus.INVITED && "Discando..."}
            {!actuallyOnCall && !connecting && status !== DialerStatus.INVITED && "Ligar"}
          </button>

          {callDetails?.features && callDetails?.features.includes("CARROSSEL") && (
            <Tooltip text={activeNumber?.called ? "Chamada já possui um status" : "Marcar ligação como não atendida"}>
              <button
                type="button"
                disabled={activeNumber?.called}
                onClick={() => markNumberNotMet()}
                className="w-12 h-10 disabled:cursor-not-allowed bg-neutral-gray-800 text-white items-center justify-center flex rounded-lg disabled:bg-neutral-gray-600 hover:bg-neutral-gray-600 transition-all"
              >
                <FaBan />
              </button>
            </Tooltip>
          )}
        </div>
      </div>
    </>
  );
}
